// Create this file at: /Users/kacey/BoxyQR_Website/BoxyQR/boxyQR_website/client/src/components/modals/BulkQRStatsModal.js

import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode.react';

function BulkQRStatsModal({ isOpen, onClose, bulkGroup }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    const fetchBulkStats = async () => {
      if (!isOpen || !bulkGroup) return;
      
      setLoading(true);
      try {
        // Fetch stats for the bulk group
        const response = await fetch(`/api/bulk-qrcodes/${bulkGroup._id}/stats`);
        if (response.ok) {
          const data = await response.json();
          console.log('Bulk stats data:', data);
          setStats(data);
        } else {
          console.error('Failed to fetch bulk stats');
        }
      } catch (error) {
        console.error('Error fetching bulk stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen && bulkGroup) {
      fetchBulkStats();
    }
  }, [isOpen, bulkGroup]);
  
  if (!isOpen || !bulkGroup) return null;
  
  // Calculate total visits
  const totalVisits = bulkGroup.totalVisits || 0;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 relative max-h-[90vh] overflow-auto">
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-xl font-bold"
        >
          ✕
        </button>
        
        <h2 className="text-xl font-bold mb-4">Bulk QR Code Group: {bulkGroup.name}</h2>
        
        <div className="mb-4 flex items-center">
          <div className="bg-gray-100 p-3 rounded-lg mr-4">
            <span className="text-3xl font-bold block text-center">{totalVisits}</span>
            <span className="text-sm text-gray-500 block text-center">Total Scans</span>
          </div>
          
          <div>
            <p className="mb-1"><strong>QR Codes:</strong> {bulkGroup.count || (bulkGroup.qrCodes ? bulkGroup.qrCodes.length : 0)}</p>
            <p className="mb-1"><strong>Created:</strong> {new Date(bulkGroup.createdAt).toLocaleDateString()}</p>
          </div>
          
          <div className="ml-auto">
            <button
              onClick={() => window.location.href = `/api/bulk-qrcodes/${bulkGroup._id}/download`}
              className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
            >
              Download All
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b mb-4">
          <div className="flex">
            <button
              className={`py-2 px-4 ${activeTab === 'overview' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`py-2 px-4 ${activeTab === 'qrcodes' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('qrcodes')}
            >
              QR Codes
            </button>
            <button
              className={`py-2 px-4 ${activeTab === 'devices' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('devices')}
            >
              Devices
            </button>
            <button
              className={`py-2 px-4 ${activeTab === 'locations' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('locations')}
            >
              Locations
            </button>
          </div>
        </div>
        
        {/* Tab content */}
        {loading ? (
          <div className="text-center py-10">Loading stats...</div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-medium mb-3">Scan Overview</h3>
                {stats ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-2xl font-bold block">{stats.todayScans || 0}</span>
                      <span className="text-sm text-gray-500">Today</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-2xl font-bold block">{stats.weekScans || 0}</span>
                      <span className="text-sm text-gray-500">This Week</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-2xl font-bold block">{stats.monthScans || 0}</span>
                      <span className="text-sm text-gray-500">This Month</span>
                    </div>
                  </div>
                ) : (
                  <p>No scan data available</p>
                )}
              </div>
            )}
            
            {/* QR Codes Tab */}
            {activeTab === 'qrcodes' && (
              <div>
                <h3 className="text-lg font-medium mb-3">Individual QR Codes</h3>
                {bulkGroup.qrCodes && bulkGroup.qrCodes.length > 0 ? (
                  <div className="overflow-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QR Code</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scans</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {bulkGroup.qrCodes.map((qr) => (
                          <tr key={qr._id}>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="w-12 h-12 flex-shrink-0">
                                <QRCode
                                  value={`${window.location.origin}/${qr.shortId}`}
                                  size={48}
                                  fgColor={bulkGroup.foregroundColor || '#000000'}
                                  bgColor={bulkGroup.backgroundColor || '#FFFFFF'}
                                  level="L"
                                  renderAs="svg"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {qr.qr_name || 'Unnamed'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {qr.visits || 0}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              <div className="truncate max-w-xs">
                                {qr.targetUrl}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No QR codes found in this group</p>
                )}
              </div>
            )}
            
            {/* Devices Tab */}
            {activeTab === 'devices' && (
              <div>
                <h3 className="text-lg font-medium mb-3">Device Breakdown</h3>
                {stats && stats.devices ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(stats.devices).map(([device, count]) => (
                      <div key={device} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                        <span className="capitalize">{device}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No device data available</p>
                )}
              </div>
            )}
            
            {/* Locations Tab */}
            {activeTab === 'locations' && (
              <div>
                <h3 className="text-lg font-medium mb-3">Scan Locations</h3>
                {stats && stats.locations && stats.locations.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {stats.locations.map((location, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                        <span>
                          {location.city ? location.city + ', ' : ''}
                          {location.region || location.country || 'Unknown'}
                        </span>
                        <span className="font-bold">{location.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No location data available</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BulkQRStatsModal;